import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { useFirebase } from '../context/FirebaseContext';

export const useFirestore = () => {
  const { db, currentUser } = useFirebase();

  // Load entries with filters for current user only
  const loadEntries = async (projectId, filters = {}) => {
    if (!projectId || projectId === '') {
      return [];
    }

    if (!currentUser) {
      return [];
    }

    try {
      // Always filter by userId first for security
      let q = query(
        collection(db, 'entries'),
        where('projectId', '==', projectId),
        where('userId', '==', currentUser.uid)
      );

      // Apply Firestore-supported filters
      if (filters.Bureau && filters.Bureau !== '') {
        q = query(q, where('bureau', '==', filters.Bureau));
      }
      if (filters.Medium && filters.Medium !== '') {
        q = query(q, where('medium', '==', filters.Medium));
      }
      if (filters.Campagne && filters.Campagne !== '') {
        q = query(q, where('campagneId', '==', filters.Campagne));
      }
      if (filters.Uiting && filters.Uiting !== '') {
        q = query(q, where('uiting', '==', filters.Uiting));
      }
      if (filters.Doelgroep && filters.Doelgroep !== '') {
        q = query(q, where('doelgroep', '==', filters.Doelgroep));
      }

      const querySnapshot = await getDocs(q);
      const results = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        results.push({
          Id: data.id,
          Url: data.url || '',
          Bureau: data.bureau || '',
          Campagne: data.campagneId || '',
          Datum: data.datum ? new Date(data.datum.seconds * 1000).toLocaleDateString('en-US') : '',
          Titel: data.titel || '',
          Medium: data.medium || '',
          Linknaam: data.linknaam || '',
          Uiting: data.uiting || '',
          Doelgroep: data.doelgroep || '',
          _firestoreId: doc.id
        });
      });

      // Client-side filtering for fields not supported in Firestore query
      let filteredResults = results;

      if (filters.Url && filters.Url !== '') {
        filteredResults = filteredResults.filter(item =>
          item.Url.toLowerCase().indexOf(filters.Url.toLowerCase()) > -1
        );
      }
      if (filters.Datum && filters.Datum !== '') {
        filteredResults = filteredResults.filter(item =>
          item.Datum.toLowerCase().indexOf(filters.Datum.toLowerCase()) > -1
        );
      }
      if (filters.Titel && filters.Titel !== '') {
        filteredResults = filteredResults.filter(item =>
          item.Titel.toLowerCase().indexOf(filters.Titel.toLowerCase()) > -1
        );
      }
      if (filters.Linknaam && filters.Linknaam !== '') {
        filteredResults = filteredResults.filter(item =>
          item.Linknaam.toLowerCase().indexOf(filters.Linknaam.toLowerCase()) > -1
        );
      }

      return filteredResults;
    } catch (error) {
      console.error('Error loading entries:', error);
      throw error;
    }
  };

  // Insert new entry
  const insertEntry = async (entry) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to create an entry');
      }

      const id = String(Date.now()) + String(Math.floor(Math.random() * 8999 + 1000));
      
      // Parse datum
      const dateParts = entry.Datum.split('/');
      const dateObj = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);

      const entryData = {
        id: id,
        url: entry.Url || '',
        bureau: entry.Bureau || '',
        campagneId: entry.Campagne || '',
        projectId: entry.Project,
        datum: Timestamp.fromDate(dateObj),
        titel: entry.Titel || '',
        medium: entry.Medium || '',
        linknaam: entry.Linknaam || '',
        uiting: entry.Uiting || '',
        doelgroep: entry.Doelgroep || '',
        userId: currentUser.uid,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'entries'), entryData);
      
      return { ...entry, Id: id, _firestoreId: docRef.id };
    } catch (error) {
      console.error('Error inserting entry:', error);
      throw error;
    }
  };

  // Update entry
  const updateEntry = async (entry) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to update an entry');
      }

      // Parse datum
      const dateParts = entry.Datum.split('/');
      const dateObj = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);

      const updateData = {
        url: entry.Url || '',
        bureau: entry.Bureau || '',
        campagneId: entry.Campagne || '',
        datum: Timestamp.fromDate(dateObj),
        titel: entry.Titel || '',
        medium: entry.Medium || '',
        linknaam: entry.Linknaam || '',
        uiting: entry.Uiting || '',
        doelgroep: entry.Doelgroep || '',
        updatedAt: serverTimestamp()
      };

      // Find document by Id field and userId for security
      const q = query(
        collection(db, 'entries'), 
        where('id', '==', entry.Id),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Entry not found or you do not have permission to update it');
      }

      const entryDoc = querySnapshot.docs[0];
      await updateDoc(entryDoc.ref, updateData);

      return entry;
    } catch (error) {
      console.error('Error updating entry:', error);
      throw error;
    }
  };

  // Delete entry
  const deleteEntry = async (entry) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to delete an entry');
      }

      const q = query(
        collection(db, 'entries'), 
        where('id', '==', entry.Id),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Entry not found or you do not have permission to delete it');
      }

      const entryDoc = querySnapshot.docs[0];
      await deleteDoc(entryDoc.ref);

      return true;
    } catch (error) {
      console.error('Error deleting entry:', error);
      throw error;
    }
  };

  // Delete multiple entries
  const deleteMultipleEntries = async (entries) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to delete entries');
      }

      const deletePromises = entries.map(async (entry) => {
        const q = query(
          collection(db, 'entries'), 
          where('id', '==', entry.Id),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          return deleteDoc(querySnapshot.docs[0].ref);
        }
      });

      await Promise.all(deletePromises);
      return { success: entries.length };
    } catch (error) {
      console.error('Error deleting multiple entries:', error);
      throw error;
    }
  };

  return {
    loadEntries,
    insertEntry,
    updateEntry,
    deleteEntry,
    deleteMultipleEntries
  };
};

export default useFirestore;

